import axios, { AxiosRequestConfig } from 'axios'
import { ApiBrandResponse, Brand } from './types/brands'
import {
  ApiProductsResponse,
  Product,
  ProductsResponseWithPagination
} from './types/product'
import { ApiConfigurationsResponse, Configuration } from './types/configuration'
import { ApiCategoriesResponse, Category } from './types/category'
import { ApiMaterialsResponse, Material } from './types/material'
import { FeedbackFormData } from '../components/Feedback/FeedbackModal'

const resourceUrl =
  process.env.NODE_ENV === 'development'
    ? process.env.REACT_APP_API_URL
    : window.env && window.env.API_URL
const deployedEnv =
  process.env.NODE_ENV === 'development'
    ? 'DEV'
    : window.env && window.env.API_ENV

const pageSize = 50

const TOKEN_STORAGE_KEY = 'access_token'
const TOKEN_EXPIRY_STORAGE_KEY = 'token_expiry'

// Helper function to get token from sessionStorage
const getStoredToken = () => {
  const token = sessionStorage.getItem(TOKEN_STORAGE_KEY)
  const expiry = sessionStorage.getItem(TOKEN_EXPIRY_STORAGE_KEY)

  if (token && expiry) {
    const now = new Date().getTime()
    if (now < parseInt(expiry, 10)) {
      return token
    }
  }
  return null
}

// Helper function to store token and expiry in sessionStorage
const storeToken = (token: string, expiresIn: number) => {
  const expiryTime = new Date().getTime() + expiresIn * 1000
  sessionStorage.setItem(TOKEN_STORAGE_KEY, token)
  sessionStorage.setItem(TOKEN_EXPIRY_STORAGE_KEY, expiryTime.toString())
}

// Function to fetch a new token
const fetchNewToken = async () => {
  try {
    const response = await fetch(
      `https://truckload-token.azurewebsites.net/api/gettoken?env=${deployedEnv}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    const token = data.token
    storeToken(token.access_token, token.expires_in)
    return token.access_token
  } catch (error) {
    console.error('Error fetching new token:', error)
    throw error
  }
}

// Function to get a valid access token (either from storage or by fetching a new one)
const getAccessToken = async () => {
  const token = getStoredToken()
  if (token) {
    return token
  }
  return await fetchNewToken()
}

// Wrapping axios request to handle token expiration
const axiosRequest = async (config: AxiosRequestConfig) => {
  if (!config.headers) {
    config.headers = {}
  }

  try {
    const accessToken = await getAccessToken()
    config.headers.Authorization = `Bearer ${accessToken}`

    return await axios(config)
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response && error.response.status === 401) {
        const newAccessToken = await fetchNewToken()
        config.headers.Authorization = `Bearer ${newAccessToken}`
        return axios(config)
      }
    }
    throw error
  }
}

export const getBrands = async () => {
  try {
    const response = await axiosRequest({
      url: `${resourceUrl}/abg_products?$filter=(abg_producttype eq 745820001 and statuscode eq 1)`,
      method: 'GET'
    })

    // console.log('response', response.data.value)

    return response.data.value.map((item: ApiBrandResponse) => ({
      id: item.abg_productid,
      name: item.abg_product_name,
      img: item.crc60_product_image,
      description: item.abg_brand_description,
      order: item.abg_brandorder
    })) as Brand[]
  } catch (error) {
    console.error('Error fetching brands:', error)
    throw error
  }
}

export const getProducts = async (
  brandId: string,
  skipToken?: string | undefined
): Promise<ProductsResponseWithPagination> => {
  try {
    const url = new URL(
      `${resourceUrl}/abg_products?$filter=_abg_brand_value eq ${brandId}&$expand=abg_product_brand($select=abg_product_name),abg_materialid($select=abg_name),abg_categoryid($select=abg_name)&$orderby=abg_product_number asc`
    )

    if (skipToken) {
      url.searchParams.append('$skiptoken', decodeURIComponent(skipToken))
    }

    const response = await axiosRequest({
      url: url.toString(),
      method: 'GET',
      headers: {
        Prefer: `odata.maxpagesize=${pageSize}`
      }
    })

    // console.log('products', response.data.value)

    const products = response.data.value.map((item: ApiProductsResponse) => ({
      id: item.abg_productid,
      productNumber: item.abg_product_number,
      partName: item.abg_product_name,
      FTLQuantity: item.abg_ftl_quantity,
      leadTime: item.abg_lead_time,
      pointValue: item.abg_pointvaluecalc,
      category: item.abg_categoryid?.abg_name
        ? item.abg_categoryid.abg_name
        : 'Missing',
      material: item.abg_materialid?.abg_name
        ? item.abg_materialid.abg_name
        : 'Missing'
    })) as Product[]

    const nextSkipToken =
      response.data['@odata.nextLink']?.split('$skiptoken=')[1] || ''

    return {
      products,
      nextSkipToken
    }
  } catch (error) {
    console.error('Error fetching products:', error)
    throw error
  }
}

export const getConfigurations = async () => {
  try {
    const response = await axiosRequest({
      url: `${resourceUrl}/abg_configurations?$select=abg_configurationid,abg_key,abg_value`,
      method: 'GET'
    })

    // console.log('response', response.data.value)

    return response.data.value.map((item: ApiConfigurationsResponse) => ({
      configurationId: item.abg_configurationid,
      description: item.abg_key,
      configValue: item.abg_value
    })) as Configuration[]
  } catch (error) {
    console.error('Error fetching Threshold Percentages:', error)
    throw error
  }
}

export const searchProducts = async ({
  searchTerm,
  brandId,
  categoryId,
  materialId
}: {
  searchTerm: string | null
  brandId: string
  categoryId: string | null
  materialId: string | null
}) => {
  // Build the filter query
  const filterConditions = [`_abg_brand_value eq ${brandId}`]

  if (materialId) {
    filterConditions.push(`_abg_materialid_value eq ${materialId}`)
  }

  if (categoryId) {
    filterConditions.push(`_abg_categoryid_value eq ${categoryId}`)
  }

  if (searchTerm) {
    const searchConditions = [
      `contains(abg_product_number, '${searchTerm}')`,
      `contains(abg_product_name, '${searchTerm}')`,
      `contains(abg_part_number, '${searchTerm}')`
    ].join(' or ')
    filterConditions.push(`(${searchConditions})`)
  }

  const filterQuery = filterConditions.join(' and ')

  const url = `${resourceUrl}/abg_products?$filter=${filterQuery}&$top=200&$expand=abg_product_brand($select=abg_product_name),abg_materialid($select=abg_name),abg_categoryid($select=abg_name)&$orderby=abg_product_number asc`

  try {
    const response = await axiosRequest({
      url: url,
      method: 'GET'
    })

    // console.log('response', response.data.value)

    return response.data.value.map((item: ApiProductsResponse) => ({
      id: item.abg_productid,
      productNumber: item.abg_product_number,
      partName: item.abg_product_name,
      leadTime: item.abg_lead_time,
      FTLQuantity: item.abg_ftl_quantity,
      pointValue: item.abg_pointvaluecalc,
      category: item.abg_categoryid?.abg_name
        ? item.abg_categoryid.abg_name
        : 'Missing',
      material: item.abg_materialid?.abg_name
        ? item.abg_materialid.abg_name
        : 'Missing'
    })) as Product[]
  } catch (error) {
    console.error('Error searching products:', error)
    throw error
  }
}

export const getCategories = async () => {
  try {
    const response = await axiosRequest({
      url: `${resourceUrl}/abg_categories`,
      method: 'GET'
    })

    // console.log('response', response.data.value)

    return response.data.value.map((item: ApiCategoriesResponse) => ({
      id: item.abg_categoryid,
      name: item.abg_name
    })) as Category[]
  } catch (error) {
    console.error('Error fetching categories:', error)
    throw error
  }
}

export const getMaterials = async () => {
  try {
    const response = await axiosRequest({
      url: `${resourceUrl}/abg_materials`,
      method: 'GET'
    })

    return response.data.value.map((item: ApiMaterialsResponse) => ({
      id: item.abg_materialid,
      name: item.abg_name
    })) as Material[]
  } catch (error) {
    console.error('Error fetching materials:', error)
    throw error
  }
}

export const submitFeedback = async (data: FeedbackFormData) => {
  // Map satisfaction scores as per the API
  const satisfactionScoreMap: Record<number, number> = {
    1: 745820000,
    2: 745820001,
    3: 745820002,
    4: 745820003,
    5: 745820004
  }

  const apiData = {
    abg_question: data.question,
    abg_comment: data.comment,
    abg_satisfactionscore: satisfactionScoreMap[data.satisfactionScore]
  }

  try {
    const response = await axiosRequest({
      url: `${resourceUrl}/abg_feedbacks`,
      method: 'POST',
      data: apiData
    })

    return {
      success: true,
      data: response.data
    }
  } catch (error) {
    console.error('Error submitting feedback:', error)
    throw new Error('Failed to submit feedback')
  }
}
